export const RUN_SHEET_PREVIEW_DETAIL_CACHE_LIMIT = 16;
const RUN_SHEET_PREVIEW_TEXT_LIMIT = 12_000;

export interface RunSheetPreviewDetail {
    objective: string;
    jobDescription: string;
    heroImage: Record<string, unknown> | null;
}

export type RunSheetPreviewDetailCache = Map<string, RunSheetPreviewDetail>;

function nonEmptyString(...values: unknown[]): string | null {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
}

function boundedText(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.slice(0, RUN_SHEET_PREVIEW_TEXT_LIMIT);
}

/**
 * The Run Sheet needs only enough identity for usePhotoSrc to recover a cloud
 * image. It must not retain legacy data URLs, browser Blob ids, Blob objects,
 * or expiring preview/signed URLs from every project opened during a session.
 */
export function sanitizeRunSheetPreviewHero(
    candidate: unknown,
): Record<string, unknown> | null {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
    const record = candidate as Record<string, unknown>;
    const s3Key = nonEmptyString(
        record.s3Key,
        record.s3_key,
        record.cloudObjectKey,
        record.cloud_object_key,
    );
    const fileMetadataId = nonEmptyString(record.fileMetadataId, record.file_metadata_id);
    if (!s3Key && !fileMetadataId) return null;

    const id = nonEmptyString(record.id);
    const photoId = nonEmptyString(record.photoId, record.photo_id);
    const name = nonEmptyString(record.name);
    const originalName = nonEmptyString(record.originalName, record.original_name);
    const contentType = nonEmptyString(record.contentType, record.content_type, record.mimeType);

    return {
        ...(id ? { id } : {}),
        ...(photoId ? { photoId } : {}),
        ...(name ? { name } : {}),
        ...(originalName ? { originalName } : {}),
        ...(contentType ? { contentType } : {}),
        ...(s3Key ? { s3Key } : {}),
        ...(fileMetadataId ? { fileMetadataId } : {}),
    };
}

export function sanitizeRunSheetPreviewDetail(
    detail: Partial<RunSheetPreviewDetail> | null | undefined,
): RunSheetPreviewDetail {
    return {
        objective: boundedText(detail?.objective),
        jobDescription: boundedText(detail?.jobDescription),
        heroImage: sanitizeRunSheetPreviewHero(detail?.heroImage),
    };
}

/** Immutable bounded insertion cache. Existing entries move to the newest end. */
export function upsertRunSheetPreviewDetailCache(
    cache: RunSheetPreviewDetailCache,
    projectId: string,
    detail: Partial<RunSheetPreviewDetail>,
    maxEntries = RUN_SHEET_PREVIEW_DETAIL_CACHE_LIMIT,
): RunSheetPreviewDetailCache {
    const next = new Map(cache);
    next.delete(projectId);
    next.set(projectId, sanitizeRunSheetPreviewDetail(detail));

    const limit = Number.isInteger(maxEntries) && maxEntries > 0
        ? maxEntries
        : RUN_SHEET_PREVIEW_DETAIL_CACHE_LIMIT;
    while (next.size > limit) {
        const oldest = next.keys().next().value as string | undefined;
        if (oldest === undefined) break;
        next.delete(oldest);
    }
    return next;
}
